import { z } from "zod";
import type { UploadContextType } from "../enum.js";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
const MAX_AUDIO_SIZE = 5 * 1024 * 1024;

const IMAGE_CONTENT_TYPES = ["image/png", "image/jpeg"] as const;
const VIDEO_CONTENT_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;
const AUDIO_CONTENT_TYPES = ["audio/mpeg", "audio/mp4", "audio/wav", "audio/webm", "audio/ogg"] as const;

const ALL_CONTENT_TYPES = [...IMAGE_CONTENT_TYPES, ...VIDEO_CONTENT_TYPES, ...AUDIO_CONTENT_TYPES] as const;

export const ALLOWED_CONTENT_TYPES_BY_CONTEXT = {
	user_avatar: [...IMAGE_CONTENT_TYPES],
	group_avatar: [...IMAGE_CONTENT_TYPES],
	post_media: [...IMAGE_CONTENT_TYPES, ...VIDEO_CONTENT_TYPES],
	story_media: [...IMAGE_CONTENT_TYPES, ...VIDEO_CONTENT_TYPES],
	conversation_media: [...ALL_CONTENT_TYPES],
} as const;

export const uploadContextSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("user_avatar") }).strict(),
	z.object({ type: z.literal("group_avatar"), entity_id: z.string().min(1) }).strict(),
	z.object({ type: z.literal("post_media"), entity_id: z.string().min(1) }).strict(),
	z.object({ type: z.literal("story_media"), entity_id: z.string().min(1) }).strict(),
	z.object({ type: z.literal("conversation_media"), entity_id: z.string().min(1) }).strict(),
]);

export type UploadContext = z.infer<typeof uploadContextSchema>;

function getMaxSize(contentType: string): number {
	if (contentType.startsWith("video/")) return MAX_VIDEO_SIZE;
	if (contentType.startsWith("audio/")) return MAX_AUDIO_SIZE;
	return MAX_IMAGE_SIZE;
}

function isContentTypeAllowed(contextType: UploadContextType, contentType: string): boolean {
	const allowed = ALLOWED_CONTENT_TYPES_BY_CONTEXT[contextType];
	return (allowed as readonly string[]).includes(contentType);
}

export const presignUploadSchema = z
	.object({
		file_name: z.string().min(1),
		content_type: z.enum(ALL_CONTENT_TYPES),
		size: z.number().positive(),
		context: uploadContextSchema,
	})
	.strict()
	.superRefine((data, ctx) => {
		if (!isContentTypeAllowed(data.context.type, data.content_type)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `Content type "${data.content_type}" is not allowed for "${data.context.type}" uploads`,
				path: ["content_type"],
			});
			return;
		}

		const maxSize = getMaxSize(data.content_type);
		if (data.size > maxSize) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `File size exceeds the maximum allowed (${Math.round(maxSize / 1024 / 1024)}MB)`,
				path: ["size"],
			});
		}
	});

export type PresignUploadPayload = z.infer<typeof presignUploadSchema>;

const batchFileSchema = z.object({
	file_name: z.string().min(1),
	content_type: z.enum(ALL_CONTENT_TYPES),
	size: z.number().positive(),
});

export const presignBatchSchema = z
	.object({
		files: z.array(batchFileSchema).min(1).max(6),
		context: uploadContextSchema,
	})
	.strict()
	.superRefine((data, ctx) => {
		for (const [index, file] of data.files.entries()) {
			if (!isContentTypeAllowed(data.context.type, file.content_type)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Content type "${file.content_type}" is not allowed for "${data.context.type}" uploads`,
					path: ["files", index, "content_type"],
				});
				continue;
			}

			const maxSize = getMaxSize(file.content_type);
			if (file.size > maxSize) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `File size exceeds the maximum allowed (${Math.round(maxSize / 1024 / 1024)}MB)`,
					path: ["files", index, "size"],
				});
			}
		}
	});

export type PresignBatchPayload = z.infer<typeof presignBatchSchema>;

export interface PresignUploadResponse {
	upload_url: string;
	public_url: string;
}
